import React from 'react';
import classnames from 'classnames';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import HoverableWrapper from '@wix/da-ds/pkg/thumbs/OverlayHelpers/HoverableWrapper';
import { useOverlayContext } from '../../../hooks/useOverlayContext';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import DeviationDumbView from '../../../Dumbo';
import DeviationLink from '../../../../Duperbrowse/DeviationLink';
import { DeviationTypes } from '../../../../types/deviation';
import { useTranslation } from 'react-i18next';
import { getTranslationKeyForAccessibleDeviationLink } from '../../helpers';
import Overlay from '../../legos/Overlay';
import { StandardThumbProps } from '../types';
import { getDeviationType } from '../../../_helpers';

import s from './Default.scss';

const Default: React.FC<StandardThumbProps> = ({
  className,
  deviation,
  height,
  responsive,
  style,
  width,
  extraActions,
  canLinkToStash,
  onRemove,
}) => {
  const { t } = useTranslation();
  const {
    showTitle: showOverlayTitle,
    showOverlay,
    __forceHoveredForStories,
  } = useOverlayContext(deviation);

  const type = getDeviationType(deviation);

  const linkAsWrapper = type === DeviationTypes.IMAGE;
  const a11yLabel = getTranslationKeyForAccessibleDeviationLink(t, deviation);

  return (
    <>
      <HoverableWrapper
        className={classnames(s['root'], className)}
        style={{
          width,
          height,
          ...style,
        }}
        __forceHoveredForStories={__forceHoveredForStories}
      >
        <ErrorBoundary debugComponent="StandardThumbDefault">
          {
            /*
            Because we don't always show a title inside of the overlay,
            we'll place this here to avoid any untitled sections polluting the document outline
          */
            !showOverlayTitle && (
              <VisuallyHidden>
                <Heading>{a11yLabel}</Heading>
              </VisuallyHidden>
            )
          }
          {linkAsWrapper ? (
            <DeviationLink
              aria-label={a11yLabel}
              deviation={deviation}
              canLinkToStash={canLinkToStash}
            >
              <DeviationDumbView
                deviation={deviation}
                width={width}
                height={height}
                responsive={responsive}
                videoIconSize={IconSize.GIANT}
                withSeoData
                // We'll rely on the link to inform screen readers
                aria-hidden="true"
              />
            </DeviationLink>
          ) : (
            <>
              <DeviationDumbView
                deviation={deviation}
                width={width}
                height={height}
                responsive={responsive}
                videoIconSize={IconSize.GIANT}
                withSeoData
                // We'll rely on the link to inform screen readers
                aria-hidden="true"
              />
              <DeviationLink
                deviation={deviation}
                className={s['link-overlay']}
                aria-label={a11yLabel}
                canLinkToStash={canLinkToStash}
              >
                <span />
              </DeviationLink>
            </>
          )}
          {showOverlay && (
            <Overlay
              deviation={deviation}
              width={width}
              height={height}
              className={s['overlay']}
              extraActions={extraActions}
              aria-hidden="true"
              onRemove={onRemove}
            />
          )}
        </ErrorBoundary>
      </HoverableWrapper>
    </>
  );
};
Default.displayName = 'Default';

export default Default;
